import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {MenuItem} from "primeng/api";
import {ConfiguratorEtlService, EtlConf} from "@tj/tj-dmp-lib-api";
import {BehaviorSubject, Observable, of, Subscription} from "rxjs";
import { filter, tap } from 'rxjs/operators';

@Component({
    selector: 'dmp-etl-export-update',
    templateUrl: './etl-export-update.component.html',
    styleUrls: ['./etl-export-update.component.scss'],
    standalone: false
})
export class EtlExportUpdateComponent implements OnInit, OnDestroy {

    public currentStep = 1;
    public id: string;
    public tenant: string;
    private steps: any;
    items: MenuItem[];
    breadcrumb: MenuItem[];
    activeIndex: number;
    complet: boolean;
    affectedPartners: string[] = [];
    public etl$ = new BehaviorSubject<EtlConf>(null);
    private routerSubscription: Subscription;

    constructor(private readonly route: ActivatedRoute,
                private readonly router: Router,
                private readonly translate: TranslateService,
                private readonly configuratorEtlService: ConfiguratorEtlService) {
    }

    ngOnInit(): void {
        this.id = this.route.snapshot.paramMap.get('id');
        this.tenant = this.route.snapshot.paramMap.get('tenant');

        this.updateBreadcrumb();
        if (this.id && this.tenant) {
            this.reloadEtl().subscribe();
        }

        this.steps = {
            0: 'description',
            1: 'search',
            2: 'param'
        };

        this.updateStepFromRoute();

        this.routerSubscription = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(() => {
            this.updateStepFromRoute();
        });
        const me = this;
        this.items = [
            {
                label: this.translate.instant('common.Description'),
                command: (event: any) => {
                    if (this.complet || (!this.complet)) {
                        me.goToStep(0);
                    }
                },
                disabled: false
            },
            {
                label: this.translate.instant('etl.export.steps.search'),
                command: (event: any) => {
                    if (this.complet) {
                        me.goToStep(1);
                    }
                },
                disabled: false
            },
            {
                label: this.translate.instant('etl.export.steps.param'),
                command: (event: any) => {
                    if (this.complet) {
                        me.goToStep(2);
                    }
                },
                disabled: false
            }
        ];
    }

    ngOnDestroy(): void {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }
    }

    /**
     * Recharge l'ETL depuis le serveur et le rediffuse aux étapes via etl$.
     *
     * Les étapes sont des routes filles de ce composant : passer de l'une à l'autre ne le
     * réinstancie pas et ne rejoue donc pas ngOnInit. Sans rechargement explicite après
     * l'enregistrement d'une étape, les suivantes continuent de lire l'EtlConf initial —
     * notamment une affectation d'affiliés périmée.
     */
    public reloadEtl(): Observable<EtlConf> {
        if (!this.id || !this.tenant) {
            return of(null);
        }
        return this.configuratorEtlService.getEtlByIdUsingGET(this.tenant, this.id).pipe(
            tap(etl => {
                this.complet = etl.complet;
                this.affectedPartners = etl.affectedPartners ?? [];
                this.updateBreadcrumb(etl.name);
                this.etl$.next(etl);
            })
        );
    }

    /** Fil d'Ariane, comme l'écran d'import : dernier segment = nom de l'export, ou « Nouvel export ». */
    private updateBreadcrumb(name?: string): void {
        this.breadcrumb = [
            {label: this.translate.instant('menu.flow.label')},
            {label: this.translate.instant('menu.flow.exports'), routerLink: '/h/flux/export/etl'},
            {label: name || this.translate.instant('etl.export.newExportTitle')}
        ];
    }

    private updateStepFromRoute(): void {
        const childRoute = this.route.firstChild;
        if (childRoute && childRoute.routeConfig) {
            const currentPath = childRoute.routeConfig.path;
            const step = Object.keys(this.steps).find(key => this.steps[key] === currentPath);
            this.currentStep = step ? parseInt(step, 10) : 0;
            this.activeIndex = this.currentStep;
        } else {
            this.currentStep = 0;
            this.activeIndex = 0;
        }
    }

    public goToNextStep(): void {
        if (this.currentStep < 3) {
            this.currentStep++;
            const nextStepUrl = this.steps[this.currentStep];
            this.activeIndex = this.currentStep;
            this.router.navigate(['/h/flux/export/etl/', this.tenant, this.id, nextStepUrl]);
        }
    }

    public goToPreviousStep(): void {
        if (this.currentStep > 0) {
            this.currentStep--;
            const nextStepUrl = this.steps[this.currentStep];
            this.activeIndex = this.currentStep;
            this.router.navigate(['/h/flux/export/etl/', this.tenant, this.id, nextStepUrl]);
        }
    }

    public goToStep(step: number): void {
        this.currentStep = step;
        const nextStepUrl = this.steps[step];
        this.activeIndex = step;
        this.router.navigate(['/h/flux/export/etl/', this.tenant, this.id, nextStepUrl]);
    }

    goToList() {
        this.router.navigate(['/h/flux/export/etl/']);
    }
}
